/**
 * BoardView — main Monday-like table view for one board.
 * Renders: header, filter bar, grouped sections with inline-editable cells,
 * drag-drop row reorder, quick row composer, keyboard nav, bulk select.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
  FunnelIcon,
  PencilIcon,
  Bars3Icon,
  ArrowsPointingOutIcon,
  TableCellsIcon,
  ViewColumnsIcon,
  CalendarDaysIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth';
import {
  useBoard, useCreateColumn, useCreateGroup, useCreateRow,
  useDeleteColumn, useDeleteGroup, useDeleteRow,
  useReorderRows, useReorderColumns, useUpdateColumn, useUpdateGroup, useUpdateRow,
  useUploadAttachments, useDeleteAttachment, clearBlobUrlCache,
} from './api';
import type { BoardAttachment, BoardColumn, BoardFull, BoardGroup, BoardRow, BoardViewType, ColumnKind, Filter, StatusOption } from './types';
import { Cell, STATUS_COLORS } from './cells';
import { resolveRoles } from './boardHelpers';
import { FilesCell } from './FilesCell';
import { KanbanView } from './KanbanView';
import { CalendarView } from './CalendarView';
import { ItemPanel } from './ItemPanel';
import { FileDrawer } from './FileDrawer';

const GROUP_COLORS = ['gray', 'blue', 'green', 'yellow', 'red', 'purple', 'pink', 'orange'];
const COLUMN_KIND_LABELS: Record<ColumnKind, string> = {
  text: 'Texte',
  status: 'Statut',
  date: 'Date',
  person: 'Personne',
  number: 'Nombre',
  dropdown: 'Liste déroulante',
  checkbox: 'Case à cocher',
};

// Plain text columns grow to fill leftover horizontal space; everything else
// (and sheet columns) keeps its fixed width.
// Effective width used both for fixed cells and as the flex-basis of text cells.
const CHECKBOX_W = 64; // a checkbox column is just a box — keep it compact
const colWidth = (col: BoardColumn): number =>
  col.kind === 'checkbox' ? CHECKBOX_W :
  col.config?.variant === 'files' ? Math.max(col.width, 240) : col.width;

const cellStyle = (col: BoardColumn): React.CSSProperties =>
  col.kind === 'text' && !col.config?.variant
    ? { flex: `1 1 ${col.width}px`, minWidth: 0 }   // grows to fill; min-width:0 so all rows stay equal
    : { width: colWidth(col), flex: `0 0 ${colWidth(col)}px`, ...(col.kind === 'checkbox' ? { justifyContent: 'center' } : null) };

// Total intrinsic width of a board's grid (control + columns + trailing cells).
const CTRL_W = 0;    // (row-select checkbox column removed)
const TRAIL_W = 132; // actions: (done) + drag-handle + open + delete
const gridMinWidth = (cols: BoardColumn[]): number =>
  CTRL_W + cols.reduce((a, c) => a + colWidth(c), 0) + TRAIL_W;

// Compare two non-empty cell values for sorting, per column kind.
function compareCellValues(a: unknown, b: unknown, col: BoardColumn): number {
  switch (col.kind) {
    case 'number': return Number(a) - Number(b);
    case 'checkbox': return (a ? 1 : 0) - (b ? 1 : 0);
    case 'date': return String(a).localeCompare(String(b)); // ISO yyyy-mm-dd → chronological
    case 'status':
    case 'dropdown': {
      const opts = (col.config?.options || []) as { id: string; label: string }[];
      const la = opts.find(o => o.id === a)?.label ?? String(a);
      const lb = opts.find(o => o.id === b)?.label ?? String(b);
      return la.localeCompare(lb, 'fr', { sensitivity: 'base' });
    }
    default: return String(a).replace(/<[^>]+>/g, '').localeCompare(String(b).replace(/<[^>]+>/g, ''), 'fr', { sensitivity: 'base' });
  }
}

// ----- Helpers -----
function rowMatchesFilters(row: BoardRow, filters: Filter[]): boolean {
  for (const f of filters) {
    const v = row.values[f.columnId];
    if (f.value === '__empty__') {
      if (v != null && v !== '') return false;
    } else if (Array.isArray(f.value)) {
      if (!f.value.includes(v as string)) return false;
    } else if (String(v ?? '').toLowerCase() !== String(f.value).toLowerCase()) {
      return false;
    }
  }
  return true;
}

// ============================================================================
//  Colour swatch picker (replaces text <select> for group / option colours)
// ============================================================================
const ColorSwatchMenu: React.FC<{ value: string; onChange: (c: string) => void; title?: string }> = ({ value, onChange, title }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const pal = STATUS_COLORS[value] || STATUS_COLORS.gray;
  return (
    <div className="bd-colorpick" ref={ref}>
      <button
        type="button"
        className="bd-colorpick-trigger"
        title={title || 'Couleur'}
        style={{ background: pal.fg }}
        onClick={() => setOpen(o => !o)}
      />
      {open && (
        <div className="bd-pop bd-colorpick-pop">
          <div className="bd-color-grid">
            {GROUP_COLORS.map(c => (
              <button
                key={c}
                type="button"
                className={`bd-color-sw ${c === value ? 'bd-color-sw--on' : ''}`}
                style={{ background: STATUS_COLORS[c].fg }}
                title={c}
                onClick={() => { onChange(c); setOpen(false); }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
//  Add / edit column modal
// ============================================================================
const ColumnModal: React.FC<{
  boardId: string;
  column?: BoardColumn;
  onClose: () => void;
}> = ({ boardId, column, onClose }) => {
  const isEdit = !!column;
  const cfg = (column?.config || {}) as { prefix?: string; suffix?: string; display?: string; variant?: string; onCheck?: string; checkColor?: string };
  const [name, setName] = useState(column?.name || '');
  // "type" includes the pseudo-types 'sheet' and 'files' (text columns with a config.variant).
  const initialType: ColumnKind | 'sheet' | 'files' =
    column ? (column.kind === 'text' && (cfg.variant === 'sheet' || cfg.variant === 'files') ? cfg.variant : column.kind) : 'text';
  const [type, setType] = useState<ColumnKind | 'sheet' | 'files'>(initialType);
  const kind: ColumnKind = type === 'sheet' || type === 'files' ? 'text' : type;
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>(
    (column?.config?.options as StatusOption[]) || [
      { id: 'opt-1', label: 'À faire', color: 'gray' },
      { id: 'opt-2', label: 'En cours', color: 'yellow' },
      { id: 'opt-3', label: 'Terminé', color: 'green' },
    ]
  );
  // Number display config (currency / progress / plain)
  const [numDisplay, setNumDisplay] = useState<'plain' | 'currency' | 'progress'>(
    (cfg.display as 'plain' | 'currency' | 'progress') || (cfg.suffix === '%' ? 'progress' : (cfg.prefix || cfg.suffix) ? 'currency' : 'plain')
  );
  const [numPrefix, setNumPrefix] = useState(cfg.prefix || '');
  const [numSuffix, setNumSuffix] = useState(cfg.display === 'progress' || cfg.suffix === '%' ? '' : (cfg.suffix || ''));
  // Checkbox "action when checked"
  const [checkAction, setCheckAction] = useState<'none' | 'color' | 'first' | 'last' | 'archive'>(
    (cfg.onCheck as 'none' | 'color' | 'first' | 'last' | 'archive') || 'none');
  const [checkColor, setCheckColor] = useState(cfg.checkColor || 'green');
  const createCol = useCreateColumn(boardId);
  const updateCol = useUpdateColumn(boardId);

  const buildConfig = () => {
    if (type === 'sheet') return { variant: 'sheet' as const };
    if (type === 'files') return { variant: 'files' as const };
    if (kind === 'status' || kind === 'dropdown') return { options: statusOptions.filter(o => o.label.trim()) };
    if (kind === 'number') {
      if (numDisplay === 'progress') return { display: 'progress' as const, suffix: '%' };
      if (numDisplay === 'currency') return { display: 'currency' as const, prefix: numPrefix || undefined, suffix: numPrefix ? undefined : (numSuffix || '€') };
      return { display: 'plain' as const };
    }
    if (kind === 'checkbox') return { onCheck: checkAction, checkColor: checkAction === 'color' ? checkColor : undefined };
    return {};
  };

  const submit = async () => {
    if (!name.trim()) return;
    const config = buildConfig();
    if (isEdit) {
      await updateCol.mutateAsync({ columnId: column!.id, name: name.trim(), config });
    } else {
      await createCol.mutateAsync({ name: name.trim(), kind, config });
    }
    onClose();
  };

  return (
    <div className="bd-modal-backdrop" onClick={onClose}>
      <div className="bd-modal" onClick={e => e.stopPropagation()}>
        <div className="bd-modal-header">
          <h3>{isEdit ? `Éditer la colonne « ${column!.name} »` : 'Nouvelle colonne'}</h3>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="bd-modal-body">
          <label className="bd-modal-label">Nom</label>
          <input
            className="bd-modal-input"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          />

          <label className="bd-modal-label">Type</label>
          <select
            className="bd-modal-input"
            value={type}
            onChange={e => setType(e.target.value as ColumnKind | 'sheet' | 'files')}
            disabled={isEdit}
            title={isEdit ? "Le type d'une colonne ne peut pas être changé après création" : undefined}
          >
            {Object.entries(COLUMN_KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            <option value="sheet">Feuille de texte</option>
            <option value="files">Documents (fichiers)</option>
          </select>

          {(kind === 'status' || kind === 'dropdown') && (
            <div className="bd-status-editor">
              <label className="bd-modal-label">Options</label>
              {statusOptions.map((o, i) => (
                <div key={o.id} className="bd-status-row">
                  <input
                    className="bd-modal-input bd-modal-input--inline"
                    value={o.label}
                    onChange={e => {
                      const next = [...statusOptions];
                      next[i] = { ...o, label: e.target.value };
                      setStatusOptions(next);
                    }}
                    placeholder={`Option ${i + 1}`}
                  />
                  <select
                    className="bd-modal-input bd-modal-input--inline bd-modal-input--narrow"
                    value={o.color || 'gray'}
                    onChange={e => {
                      const next = [...statusOptions];
                      next[i] = { ...o, color: e.target.value };
                      setStatusOptions(next);
                    }}
                  >
                    {Object.keys(STATUS_COLORS).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button
                    className="bd-icon-btn"
                    onClick={() => setStatusOptions(statusOptions.filter(x => x.id !== o.id))}
                    title="Supprimer"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                className="bd-btn-ghost"
                onClick={() => setStatusOptions([...statusOptions, {
                  id: `opt-${Date.now()}`, label: '', color: 'gray',
                }])}
              >
                + Ajouter une option
              </button>
            </div>
          )}

          {kind === 'number' && (
            <div className="bd-status-editor">
              <label className="bd-modal-label">Affichage</label>
              <select
                className="bd-modal-input"
                value={numDisplay}
                onChange={e => setNumDisplay(e.target.value as 'plain' | 'currency' | 'progress')}
              >
                <option value="plain">Nombre</option>
                <option value="currency">Devise / unité</option>
                <option value="progress">Barre de progression (%)</option>
              </select>
              {numDisplay === 'currency' && (
                <div className="bd-status-row" style={{ marginTop: 8 }}>
                  <input
                    className="bd-modal-input bd-modal-input--inline bd-modal-input--narrow"
                    value={numPrefix}
                    onChange={e => { setNumPrefix(e.target.value); }}
                    placeholder="Préfixe ($)"
                  />
                  <input
                    className="bd-modal-input bd-modal-input--inline bd-modal-input--narrow"
                    value={numSuffix}
                    onChange={e => { setNumSuffix(e.target.value); }}
                    placeholder="Suffixe (€)"
                    disabled={!!numPrefix}
                  />
                  <span className="bd-cell-placeholder" style={{ fontSize: 12 }}>par défaut « € »</span>
                </div>
              )}
            </div>
          )}

          {kind === 'checkbox' && (
            <div className="bd-status-editor">
              <label className="bd-modal-label">Action quand la case est cochée</label>
              <select
                className="bd-modal-input"
                value={checkAction}
                onChange={e => setCheckAction(e.target.value as 'none' | 'color' | 'first' | 'last' | 'archive')}
              >
                <option value="none">Aucune</option>
                <option value="color">Colorer la ligne</option>
                <option value="first">Placer la ligne en premier</option>
                <option value="last">Placer la ligne en dernier</option>
                <option value="archive">Archiver (griser la ligne)</option>
              </select>
              {checkAction === 'color' && (
                <div className="bd-status-row" style={{ marginTop: 8, alignItems: 'center' }}>
                  <span className="bd-cell-placeholder" style={{ fontSize: 12.5 }}>Couleur de la ligne :</span>
                  <ColorSwatchMenu value={checkColor} title="Couleur de la ligne" onChange={setCheckColor} />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="bd-modal-footer">
          <button className="bd-btn-ghost" onClick={onClose}>Annuler</button>
          <button
            className="bd-btn-primary"
            onClick={submit}
            disabled={!name.trim() || createCol.isPending || updateCol.isPending}
          >
            {isEdit ? 'Enregistrer' : 'Créer'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
//  Filter bar
// ============================================================================
const FilterBar: React.FC<{
  columns: BoardColumn[];
  filters: Filter[];
  onChange: (f: Filter[]) => void;
}> = ({ columns, filters, onChange }) => {
  const [addingColumn, setAddingColumn] = useState<string>('');
  const filterable = columns.filter(c =>
    c.kind === 'status' || c.kind === 'dropdown' || c.kind === 'text' || c.kind === 'person'
  );

  return (
    <div className="bd-filter-bar">
      <FunnelIcon className="w-4 h-4 bd-filter-icon" />
      {filters.length === 0 && <span className="bd-filter-empty">Aucun filtre</span>}
      {filters.map((f, i) => {
        const col = columns.find(c => c.id === f.columnId);
        if (!col) return null;
        const label =
          col.kind === 'status' || col.kind === 'dropdown'
            ? ((col.config?.options as { id: string; label: string }[]) || [])
                .find(o => o.id === f.value)?.label || String(f.value)
            : String(f.value);
        return (
          <span key={i} className="bd-filter-chip">
            <strong>{col.name}:</strong> {label}
            <button onClick={() => onChange(filters.filter((_, j) => j !== i))}>
              <XMarkIcon className="w-3 h-3" />
            </button>
          </span>
        );
      })}
      <select
        className="bd-filter-add"
        value={addingColumn}
        onChange={e => setAddingColumn(e.target.value)}
      >
        <option value="">+ Filtrer par…</option>
        {filterable.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {addingColumn && (() => {
        const col = columns.find(c => c.id === addingColumn);
        if (!col) return null;
        if (col.kind === 'status' || col.kind === 'dropdown') {
          const opts = (col.config?.options as { id: string; label: string }[]) || [];
          return (
            <select
              className="bd-filter-value"
              autoFocus
              onChange={e => {
                if (e.target.value) onChange([...filters, { columnId: col.id, value: e.target.value }]);
                setAddingColumn('');
              }}
              defaultValue=""
            >
              <option value="">Choisir…</option>
              {opts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          );
        }
        return (
          <input
            className="bd-filter-value"
            placeholder="Valeur"
            autoFocus
            onKeyDown={e => {
              if (e.key === 'Enter') {
                const v = (e.target as HTMLInputElement).value.trim();
                if (v) onChange([...filters, { columnId: col.id, value: v }]);
                setAddingColumn('');
              }
            }}
          />
        );
      })()}
    </div>
  );
};

// ============================================================================
//  Sortable row
// ============================================================================
interface RowProps {
  row: BoardRow;
  board: BoardFull;
  onDeleteRow: (id: string) => void;
  onOpenItem: (id: string) => void;
  onOpenFile: (a: BoardAttachment, siblings: BoardAttachment[]) => void;
  onUploadToRow: (rowId: string, files: FileList) => void;
  uploadingRowId: string | null;
  decoration?: { bg?: string; archived?: boolean; mine?: boolean };
  onMarkDone?: (rowId: string, done: boolean) => void;
}

const SortableRow: React.FC<RowProps> = ({ row, board, onDeleteRow, onOpenItem, onOpenFile, onUploadToRow, uploadingRowId, decoration, onMarkDone }) => {
  const updateRow = useUpdateRow(board.id);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    data: { type: 'row', row },
  });

  const onCellChange = (columnId: string, value: unknown) => {
    updateRow.mutate({ rowId: row.id, values: { [columnId]: value } });
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    background: decoration?.bg,
  };

  return (
    <div ref={setNodeRef} style={style} className={`bd-row ${decoration?.archived ? 'bd-row--archived' : ''} ${decoration?.mine ? 'bd-row--mine' : ''}`}>
      {board.columns.map(col => (
        <div
          key={col.id}
          className="bd-cell"
          style={cellStyle(col)}
          data-row-id={row.id}
          data-col-id={col.id}
        >
          {col.kind === 'text' && col.config?.variant === 'files' ? (
            <FilesCell
              boardId={board.id}
              attachments={row.attachments || []}
              onOpen={a => onOpenFile(a, row.attachments || [])}
              onUpload={files => onUploadToRow(row.id, files)}
              uploading={uploadingRowId === row.id}
            />
          ) : (
            <Cell
              column={col}
              value={row.values[col.id]}
              onChange={v => onCellChange(col.id, v)}
            />
          )}
        </div>
      ))}
      <div className="bd-cell bd-cell--actions">
        {decoration?.mine && (
          <button className="bd-icon-btn bd-mark-done" title="Marquer comme fait (pour moi)" onClick={() => onMarkDone?.(row.id, true)}>
            <CheckIcon className="w-4 h-4" />
          </button>
        )}
        <button className="bd-icon-btn bd-drag-handle-btn" title="Glisser pour déplacer la ligne" {...attributes} {...listeners}>
          <Bars3Icon className="w-4 h-4" />
        </button>
        <button className="bd-icon-btn bd-row-open" onClick={() => onOpenItem(row.id)} title="Ouvrir le dossier">
          <ArrowsPointingOutIcon className="w-4 h-4" />
        </button>
        <button
          className="bd-icon-btn"
          onClick={() => { if (confirm('Supprimer cette ligne ?')) onDeleteRow(row.id); }}
          title="Supprimer"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
//  Quick row composer (inline input that creates row on Enter)
// ============================================================================
const QuickRowComposer: React.FC<{
  board: BoardFull;
  groupId: string | null;
}> = ({ board, groupId }) => {
  const [draft, setDraft] = useState('');
  const createRow = useCreateRow(board.id);
  // Find the first text-ish column to pre-fill on Enter
  const firstWriteable = board.columns.find(
    c => c.kind === 'text' || c.kind === 'number' || c.kind === 'person'
  );

  const submit = () => {
    const v = draft.trim();
    if (!v) {
      createRow.mutate({ groupId, values: {} });
    } else if (firstWriteable) {
      createRow.mutate({ groupId, values: { [firstWriteable.id]: v } });
    } else {
      createRow.mutate({ groupId, values: {} });
    }
    setDraft('');
  };

  return (
    <div className="bd-row bd-row--composer">
      <div className="bd-cell bd-cell--composer">
        <PlusIcon className="w-4 h-4 bd-composer-icon" />
        <input
          className="bd-composer-input"
          placeholder={firstWriteable
            ? `Ajouter une ligne — la valeur va dans « ${firstWriteable.name} »`
            : 'Ajouter une ligne'}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); submit(); }
            else if (e.key === 'Escape') setDraft('');
          }}
        />
        <button
          className="bd-validate-btn"
          title="Ajouter la ligne (Entrée)"
          onClick={submit}
          disabled={createRow.isPending}
        >
          <CheckIcon className="w-3.5 h-3.5" /> Ajouter
        </button>
      </div>
    </div>
  );
};

// ============================================================================
//  Group section (with DnD context)
// ============================================================================
const GroupSection: React.FC<{
  group: BoardGroup | null;
  board: BoardFull;
  rows: BoardRow[];
  onEditColumn: (col: BoardColumn) => void;
  onDeleteColumn: (id: string) => void;
  onRequestAddColumn: () => void;         // user clicked "+" to add a column (opens the modal)
  onOpenItem: (id: string) => void;
  onOpenFile: (a: BoardAttachment, siblings: BoardAttachment[]) => void;
  onUploadToRow: (rowId: string, files: FileList) => void;
  uploadingRowId: string | null;
  reorderMode: boolean;
  onReorderColumn: (fromId: string, toId: string) => void;
  resizingCol: string | null;
  onResizeStart: (colId: string) => void;
  onResizeLive: (colId: string, width: number) => void;
  onResizeCommit: (colId: string, width: number) => void;
  currentUserId: string | null;
  doneRows: Set<string>;
  onMarkDone: (rowId: string, done: boolean) => void;
  sort: { colId: string; dir: 'asc' | 'desc' } | null;
  onSort: (colId: string) => void;
}> = ({ group, board, rows, onEditColumn, onDeleteColumn, onRequestAddColumn, onOpenItem, onOpenFile, onUploadToRow, uploadingRowId, reorderMode, onReorderColumn, resizingCol, onResizeStart, onResizeLive, onResizeCommit, currentUserId, doneRows, onMarkDone, sort, onSort }) => {
  const updateGroup = useUpdateGroup(board.id);
  const deleteGroup = useDeleteGroup(board.id);
  const deleteRow = useDeleteRow(board.id);
  const reorderRows = useReorderRows(board.id);

  const collapsed = group?.is_collapsed ?? false;
  const palette = group ? STATUS_COLORS[group.color] || STATUS_COLORS.gray : STATUS_COLORS.gray;

  // distance: 5 = small drag threshold so clicks inside cells still register as clicks.
  // The pointer event lives on the drag-handle cell only (not the whole row), so this
  // mostly affects the handle itself.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = rows.findIndex(r => r.id === active.id);
    const newIdx = rows.findIndex(r => r.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    const next = arrayMove(rows, oldIdx, newIdx);
    reorderRows.mutate(next.map((r, i) => ({ id: r.id, position: i, group_id: group?.id || null })));
  };

  // Checkbox "action when checked": float/grey/colour checked rows at render time.
  const actionCol = board.columns.find(c => c.kind === 'checkbox' && c.config?.onCheck && c.config.onCheck !== 'none');
  const action = actionCol?.config?.onCheck;
  const isChecked = (r: BoardRow) => actionCol ? !!r.values[actionCol.id] : false;
  const sortCol = sort ? board.columns.find(c => c.id === sort.colId) : null;
  const displayRows = (() => {
    // Explicit column sort takes precedence over the checkbox-action float.
    if (sortCol && sort) {
      const dir = sort.dir === 'asc' ? 1 : -1;
      return [...rows].sort((a, b) => {
        const va = a.values[sortCol.id], vb = b.values[sortCol.id];
        const ea = va == null || va === '', eb = vb == null || vb === '';
        if (ea && eb) return 0;
        if (ea) return 1;      // empties always last
        if (eb) return -1;
        return dir * compareCellValues(va, vb, sortCol);
      });
    }
    if (!actionCol || action === 'color') return rows;
    const checked = rows.filter(isChecked), rest = rows.filter(r => !isChecked(r));
    return action === 'first' ? [...checked, ...rest] : [...rest, ...checked]; // 'last' | 'archive'
  })();
  // Highlight rows where the current user is assigned (any person column == me)
  // and they haven't marked it done.
  const personCols = board.columns.filter(c => c.kind === 'person');
  const isMine = (r: BoardRow) =>
    !!currentUserId && !doneRows.has(r.id) && personCols.some(c => String(r.values[c.id] ?? '') === currentUserId);
  const decorate = (r: BoardRow): { bg?: string; archived?: boolean; mine?: boolean } | undefined => {
    let d: { bg?: string; archived?: boolean; mine?: boolean } | undefined;
    if (actionCol && isChecked(r)) {
      if (action === 'color') d = { bg: (STATUS_COLORS[actionCol.config?.checkColor || 'green'] || STATUS_COLORS.green).bg };
      else if (action === 'archive') d = { archived: true };
    }
    if (isMine(r)) d = { ...(d || {}), mine: true };
    return d;
  };

  return (
    <div className="bd-group">
      <div className="bd-group-header" style={{ borderLeftColor: palette.fg }}>
        <button
          className="bd-group-toggle"
          onClick={() => group && updateGroup.mutate({ groupId: group.id, isCollapsed: !collapsed })}
          disabled={!group}
        >
          {collapsed ? <ChevronRightIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
        </button>
        <span
          className="bd-group-name"
          style={{ color: palette.fg }}
          onBlur={e => {
            const name = e.currentTarget.textContent?.trim();
            if (group && name && name !== group.name) updateGroup.mutate({ groupId: group.id, name });
          }}
          contentEditable={!!group}
          suppressContentEditableWarning
        >
          {group ? group.name : 'Sans groupe'}
        </span>
        <span className="bd-group-count">{rows.length}</span>
        {group && (
          <ColorSwatchMenu
            value={group.color}
            title="Couleur du groupe"
            onChange={c => updateGroup.mutate({ groupId: group.id, color: c })}
          />
        )}
        {group && (
          <button
            className="bd-icon-btn"
            onClick={() => { if (confirm(`Supprimer le groupe "${group.name}" ? Les lignes seront déplacées dans "Sans groupe".`)) deleteGroup.mutate(group.id); }}
            title="Supprimer groupe"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="bd-table">
          <div className="bd-grid" style={{ minWidth: gridMinWidth(board.columns) }}>
          <div className="bd-row bd-row--header">
            {board.columns.map(col => (
              <div
                key={col.id}
                className={`bd-cell bd-cell--header ${reorderMode ? 'bd-cell--reorder' : 'bd-cell--sortable'}`}
                style={cellStyle(col)}
                draggable={reorderMode && resizingCol !== col.id}
                onDragStart={reorderMode ? (e) => { e.dataTransfer.setData('text/bd-col', col.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
                onDragOver={reorderMode ? (e) => { e.preventDefault(); e.currentTarget.classList.add('bd-cell--dropover'); } : undefined}
                onDragLeave={reorderMode ? (e) => { e.currentTarget.classList.remove('bd-cell--dropover'); } : undefined}
                onDrop={reorderMode ? (e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('bd-cell--dropover');
                  const from = e.dataTransfer.getData('text/bd-col');
                  if (from) onReorderColumn(from, col.id);
                } : undefined}
                onClick={!reorderMode ? () => onSort(col.id) : undefined}
                title={!reorderMode ? 'Trier par cette colonne' : undefined}
              >
                {reorderMode && <Bars3Icon className="bd-col-drag" />}
                <span className="bd-header-name">{col.name}</span>
                {!reorderMode && sort?.colId === col.id && (
                  <span className="bd-sort-ind">{sort.dir === 'asc' ? '↑' : '↓'}</span>
                )}
                {reorderMode && (
                  <span className="bd-header-actions bd-header-actions--reorder">
                    <button
                      className="bd-icon-btn"
                      title={`Éditer "${col.name}" (${COLUMN_KIND_LABELS[col.kind]})`}
                      onClick={(e) => { e.stopPropagation(); onEditColumn(col); }}
                      onPointerDown={e => e.stopPropagation()}
                    >
                      <PencilIcon className="w-3 h-3" />
                    </button>
                    <button
                      className="bd-icon-btn"
                      title="Supprimer colonne"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Supprimer la colonne "${col.name}" ?`)) onDeleteColumn(col.id);
                      }}
                      onPointerDown={e => e.stopPropagation()}
                    >
                      <TrashIcon className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {reorderMode && col.kind !== 'checkbox' && (
                  <span
                    className="bd-col-resize"
                    title="Glisser pour redimensionner"
                    onPointerDown={(e) => {
                      e.preventDefault(); e.stopPropagation();
                      onResizeStart(col.id);
                      const startX = e.clientX, startW = colWidth(col);
                      const move = (ev: PointerEvent) => onResizeLive(col.id, Math.max(80, startW + (ev.clientX - startX)));
                      const up = (ev: PointerEvent) => {
                        window.removeEventListener('pointermove', move);
                        window.removeEventListener('pointerup', up);
                        onResizeCommit(col.id, Math.max(80, startW + (ev.clientX - startX)));
                      };
                      window.addEventListener('pointermove', move);
                      window.addEventListener('pointerup', up);
                    }}
                  />
                )}
              </div>
            ))}
            <div className="bd-cell bd-cell--actions bd-cell--addcol">
              <button
                className="bd-icon-btn bd-addcol-btn"
                title="Ajouter une colonne"
                onClick={onRequestAddColumn}
              >
                <PlusIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={displayRows.map(r => r.id)} strategy={verticalListSortingStrategy}>
              {displayRows.map(row => (
                <SortableRow
                  key={row.id}
                  row={row}
                  board={board}
                  onDeleteRow={(id) => deleteRow.mutate(id)}
                  onOpenItem={onOpenItem}
                  onOpenFile={onOpenFile}
                  onUploadToRow={onUploadToRow}
                  uploadingRowId={uploadingRowId}
                  decoration={decorate(row)}
                  onMarkDone={onMarkDone}
                />
              ))}
            </SortableContext>
          </DndContext>

          <QuickRowComposer board={board} groupId={group?.id || null} />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
//  Main board view
// ============================================================================
const VIEW_TABS: { id: BoardViewType; label: string; Icon: React.FC<{ className?: string }> }[] = [
  { id: 'table',    label: 'Tableau',    Icon: TableCellsIcon },
  { id: 'kanban',   label: 'Kanban',     Icon: ViewColumnsIcon },
  { id: 'calendar', label: 'Calendrier', Icon: CalendarDaysIcon },
];

export const BoardView: React.FC<{ boardId: string; openRowId?: string | null }> = ({ boardId, openRowId }) => {
  const { data: board, isLoading } = useBoard(boardId);
  const { user } = useAuth();
  const currentUserId = user?.id ? String(user.id) : null;
  // Rows the current user marked "done" → no longer highlighted for them.
  // Per-user, persisted in localStorage.
  const doneKey = `bd-done-${currentUserId || 'anon'}`;
  const [doneRows, setDoneRows] = useState<Set<string>>(new Set());
  useEffect(() => {
    try { setDoneRows(new Set(JSON.parse(localStorage.getItem(doneKey) || '[]'))); }
    catch { setDoneRows(new Set()); }
  }, [doneKey]);
  const markRowDone = useCallback((rowId: string, done: boolean) => {
    setDoneRows(prev => {
      const n = new Set(prev);
      if (done) n.add(rowId); else n.delete(rowId);
      try { localStorage.setItem(doneKey, JSON.stringify([...n])); } catch { /* ignore */ }
      return n;
    });
  }, [doneKey]);
  const createGroup = useCreateGroup(boardId);
  const createColumn = useCreateColumn(boardId);
  const deleteColumn = useDeleteColumn(boardId);
  const updateColumn = useUpdateColumn(boardId);
  const reorderColumns = useReorderColumns(boardId);
  const qc = useQueryClient();
  const [reorderMode, setReorderMode] = useState(false);
  const [resizingCol, setResizingCol] = useState<string | null>(null);
  // Column sort (asc → desc → none on repeated header clicks).
  const [sort, setSort] = useState<{ colId: string; dir: 'asc' | 'desc' } | null>(null);
  const onSort = (colId: string) => setSort(prev =>
    prev?.colId !== colId ? { colId, dir: 'asc' } : prev.dir === 'asc' ? { colId, dir: 'desc' } : null);

  // Live column resize: patch the cache while dragging (instant feedback across
  // header + all rows), persist the final width on release.
  const resizeColumnLive = (columnId: string, width: number) => {
    const key = ['team-boards', boardId];
    const prev = qc.getQueryData<BoardFull>(key);
    if (prev) qc.setQueryData<BoardFull>(key, {
      ...prev,
      columns: prev.columns.map(c => c.id === columnId ? { ...c, width } : c),
    });
  };
  const resizeColumnCommit = (columnId: string, width: number) => {
    setResizingCol(null);
    updateColumn.mutate({ columnId, width });
  };
  const updateRow = useUpdateRow(boardId);
  const createRow = useCreateRow(boardId);
  const uploadAtt = useUploadAttachments(boardId);
  const deleteAtt = useDeleteAttachment(boardId);
  const [editingCol, setEditingCol] = useState<BoardColumn | null>(null);
  const [creatingColumn, setCreatingColumn] = useState(false);
  const [filters, setFilters] = useState<Filter[]>([]);

  // View (table/kanban/calendar) is a per-board client-side preference.
  const [view, setView] = useState<BoardViewType>('table');
  const [openItemId, setOpenItemId] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<{ file: BoardAttachment; siblings: BoardAttachment[] } | null>(null);
  const [uploadingRowId, setUploadingRowId] = useState<string | null>(null);

  const changeView = (v: BoardViewType) => {
    setView(v);
    try { localStorage.setItem(`bd-view-${boardId}`, v); } catch { /* ignore */ }
  };

  // Reset per-board UI state when the board changes; revoke any blob URLs
  // cached for the board we're leaving so they don't leak for the session.
  useEffect(() => {
    setOpenItemId(null); setDrawer(null); setSort(null);
    try { setView((localStorage.getItem(`bd-view-${boardId}`) as BoardViewType) || 'table'); }
    catch { setView('table'); }
    return () => clearBlobUrlCache(boardId);
  }, [boardId]);

  const uploadToRow = useCallback((rowId: string, files: FileList) => {
    setUploadingRowId(rowId);
    uploadAtt.mutate({ rowId, files }, { onSettled: () => setUploadingRowId(null) });
  }, [uploadAtt]);

  const openFile = useCallback((file: BoardAttachment, siblings: BoardAttachment[]) => {
    setDrawer({ file, siblings });
  }, []);

  // Deep-link: open the row's item panel once when arriving from a notification.
  const consumedRow = useRef<string | null>(null);
  useEffect(() => {
    if (openRowId && openRowId !== consumedRow.current && board?.rows.some(r => r.id === openRowId)) {
      setOpenItemId(openRowId);
      consumedRow.current = openRowId;
    }
  }, [openRowId, board]);

  const filteredRows = useMemo(() => {
    if (!board) return [];
    return board.rows.filter(r => rowMatchesFilters(r, filters));
  }, [board, filters]);

  const rowsByGroup = useMemo(() => {
    const m = new Map<string | null, BoardRow[]>();
    if (!board) return m;
    for (const r of filteredRows) {
      const k = r.group_id;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(r);
    }
    return m;
  }, [board, filteredRows]);

  // Keyboard navigation between cells: Tab / Shift+Tab traverses inputs.
  // The browser's default Tab is already row→col friendly because cells are
  // rendered in DOM order; we just stop Tab from leaving the table when
  // possible by letting native focus order work.

  if (isLoading || !board) return <div className="bd-loading">Chargement…</div>;

  const sections: { group: BoardGroup | null; rows: BoardRow[] }[] = [
    ...board.groups.map(g => ({ group: g, rows: rowsByGroup.get(g.id) || [] })),
  ];
  if (rowsByGroup.has(null)) {
    sections.push({ group: null, rows: rowsByGroup.get(null) || [] });
  }

  // ---- semantic columns + cross-view interactions ----
  const roles = resolveRoles(board.columns);

  const moveStatus = (rowId: string, statusId: string | null) => {
    if (!roles.statusCol) return;
    updateRow.mutate({ rowId, values: { [roles.statusCol.id]: statusId } });
  };
  const reschedule = (rowId: string, iso: string) => {
    if (!roles.dateCol) return;
    updateRow.mutate({ rowId, values: { [roles.dateCol.id]: iso } });
  };
  const createOn = (iso: string) => {
    if (!roles.dateCol) return;
    const groupId = board.groups[0]?.id ?? null;
    createRow.mutate(
      { groupId, values: { [roles.dateCol.id]: iso } },
      { onSuccess: (r: BoardRow) => setOpenItemId(r.id) }
    );
  };
  const updateStatusOptions = (options: StatusOption[]) => {
    if (!roles.statusCol) return;
    updateColumn.mutate({ columnId: roles.statusCol.id, config: { ...roles.statusCol.config, options } });
  };
  const createStatusColumn = () => createColumn.mutate({
    name: 'Statut', kind: 'status',
    config: { options: [
      { id: 'opt-1', label: 'À planifier', color: 'gray' },
      { id: 'opt-2', label: 'En cours', color: 'yellow' },
      { id: 'opt-3', label: 'Terminé', color: 'green' },
    ] },
  });
  const createDateColumn = () => createColumn.mutate({ name: 'Échéance', kind: 'date', config: {} });
  const reorderColumn = (fromId: string, toId: string) => {
    const ids = board.columns.map(c => c.id);
    const from = ids.indexOf(fromId), to = ids.indexOf(toId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...ids];
    next.splice(to, 0, next.splice(from, 1)[0]);
    reorderColumns.mutate(next);
  };

  // Horizontal scroll from anywhere in the board (incl. empty space / outside a
  // table): a horizontal trackpad gesture or Shift+wheel scrolls all group
  // tables in sync.
  const onBoardWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    const dx = e.deltaX || (e.shiftKey ? e.deltaY : 0);
    if (!dx) return;
    const tables = e.currentTarget.querySelectorAll<HTMLElement>('.bd-table');
    let scrolled = false;
    tables.forEach(t => { if (t.scrollWidth > t.clientWidth + 1) { t.scrollLeft += dx; scrolled = true; } });
    if (scrolled) e.preventDefault();
  };

  const itemRow = openItemId ? board.rows.find(r => r.id === openItemId) || null : null;
  const itemGroup = itemRow ? board.groups.find(g => g.id === itemRow.group_id) || null : null;

  const counts = `${filteredRows.length} dossier${filteredRows.length !== 1 ? 's' : ''} · ${board.groups.length} groupe${board.groups.length !== 1 ? 's' : ''}`;

  return (
    <div className="bd-board" onWheel={onBoardWheel}>
      {(editingCol || creatingColumn) && (
        <ColumnModal
          boardId={boardId}
          column={editingCol || undefined}
          onClose={() => { setEditingCol(null); setCreatingColumn(false); }}
        />
      )}

      <div className="bd-board-header">
        <div>
          <h2 className="bd-board-title">{board.name}</h2>
          <p className="bd-board-desc">{board.description || counts}</p>
        </div>
        <div className="bd-board-actions">
          {view === 'table' && (
            <>
              <button
                className={`bd-btn ${reorderMode ? 'bd-btn--toggled' : ''}`}
                onClick={() => setReorderMode(m => !m)}
                title="Réorganiser les colonnes par glisser-déposer"
              >
                <Bars3Icon className="w-4 h-4" /> {reorderMode ? 'Terminer' : 'Réorganiser'}
              </button>
              <button className="bd-btn" onClick={() => setCreatingColumn(true)}>
                <PlusIcon className="w-4 h-4" /> Colonne
              </button>
              <button
                className="bd-btn-primary"
                onClick={() => createGroup.mutate({ name: 'Nouveau groupe', color: 'blue' })}
              >
                <PlusIcon className="w-4 h-4" /> Groupe
              </button>
            </>
          )}
          <div className="bd-viewtabs">
            {VIEW_TABS.map(t => {
              const TIcon = t.Icon;
              return (
                <button
                  key={t.id}
                  className={`bd-viewtab ${view === t.id ? 'bd-viewtab--active' : ''}`}
                  onClick={() => changeView(t.id)}
                >
                  <TIcon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {view === 'table' && <FilterBar columns={board.columns} filters={filters} onChange={setFilters} />}

      {view === 'table' && (
        <>
          {sections.length === 0 && (
            <div className="bd-empty">
              Aucun groupe. <button className="bd-btn-link" onClick={() => createGroup.mutate({ name: 'Premier groupe', color: 'blue' })}>Créer un groupe</button>
            </div>
          )}

          {sections.map(({ group, rows }) => {
            const anchorKey = group?.id || '__none__';
            return (
              <GroupSection
                key={anchorKey}
                group={group}
                board={board}
                rows={rows}
                onEditColumn={setEditingCol}
                onDeleteColumn={(id) => deleteColumn.mutate(id)}
                onRequestAddColumn={() => setCreatingColumn(true)}
                onOpenItem={setOpenItemId}
                onOpenFile={openFile}
                onUploadToRow={uploadToRow}
                uploadingRowId={uploadingRowId}
                reorderMode={reorderMode}
                onReorderColumn={reorderColumn}
                resizingCol={resizingCol}
                onResizeStart={setResizingCol}
                onResizeLive={resizeColumnLive}
                onResizeCommit={resizeColumnCommit}
                currentUserId={currentUserId}
                doneRows={doneRows}
                onMarkDone={markRowDone}
                sort={sort}
                onSort={onSort}
              />
            );
          })}
        </>
      )}

      {view === 'kanban' && (
        <KanbanView
          boardId={boardId}
          board={board}
          statusCol={roles.statusCol}
          rows={filteredRows}
          onMoveStatus={moveStatus}
          onOpenItem={setOpenItemId}
          onUpdateStatusOptions={updateStatusOptions}
          onCreateStatusColumn={createStatusColumn}
        />
      )}

      {view === 'calendar' && (
        <CalendarView
          board={board}
          dateCol={roles.dateCol}
          rows={filteredRows}
          onReschedule={reschedule}
          onOpenItem={setOpenItemId}
          onCreateOn={createOn}
          onCreateDateColumn={createDateColumn}
        />
      )}

      {itemRow && (
        <ItemPanel
          boardId={boardId}
          board={board}
          row={itemRow}
          group={itemGroup}
          onCellChange={(columnId, value) => updateRow.mutate({ rowId: itemRow.id, values: { [columnId]: value } })}
          onUpload={(files) => uploadToRow(itemRow.id, files)}
          onDeleteAttachment={(attId) => deleteAtt.mutate(attId)}
          onOpenFile={openFile}
          onClose={() => setOpenItemId(null)}
          uploading={uploadingRowId === itemRow.id}
        />
      )}

      {drawer && (
        <FileDrawer
          boardId={boardId}
          file={drawer.file}
          siblings={drawer.siblings}
          onClose={() => setDrawer(null)}
          onSelect={(f) => setDrawer({ file: f, siblings: drawer.siblings })}
        />
      )}
    </div>
  );
};
